import { v4 as uuidv4 } from 'uuid';

import { Node } from './types';

// --- Types ---

export interface StageTreeNode {
  name: string;
  displayName: string;
  decision: string;
  debit: number | null;
  ruleIds: number[];
  stageIndex: number;
  type: 'superset' | 'workflow';
  children: StageTreeNode[];
}

export interface HitInfo {
  decision: string;
  debit: number | null;
  outcomes: Record<string, string | string[] | number>;
  ruleIds: number[];
  stageIndex: number;
  rawName: string;
}

export interface SnapshotInfo {
  index: number;
  contextKey: string;
  contextDisplayName: string;
  decision: string;
  debits: number;
}

interface TraceCondition {
  id: number;
  fact_id: number;
  fact_name: string;
  attribute_id: number;
  attribute_name: string;
  src: string;
  type: string;
  op: string;
  value: string;
  matched_value: string[];
  raw_fact_index: number[];
}

interface TraceEntry {
  rule_id: number;
  match_count: number;
  ruleset: string;
  ruleset_ref_name: string;
  conditions: TraceCondition[];
  outcome: Record<string, string | string[]>;
  ruleset_version: number;
}

interface StageNode {
  outcome: Record<string, string | string[]>;
  rules: number[];
}

// --- Constants ---

// Keys are 0-indexed iota values from janus/objects/outcome_type.go (OutcomeObjIDToEnum = ID - 1)
export const OUTCOME_KEYS: Record<string, string> = {
  '0': 'Decision',
  '1': 'Health Class',
  '2': 'Tobacco Rating',
  '3': 'Flat Extra',
  '4': 'Debits',
  '5': 'Risk Name',
  '6': 'Risk Type',
  '7': 'Next Rule Set',
  '8': 'Requirements',
  '9': 'Reason',
  '10': 'Products',
  '11': 'Report MIB Codes',
  '12': 'Group Name',
  '13': 'Rate For Tobacco',
  '14': 'Exclusions',
  '15': 'Rating Duration',
  '16': 'Rating Type',
  '17': 'Aura Decision',
  '18': 'Postpone Months',
  '19': 'Max Coverage',
  '20': 'Class Downgrade',
  '21': 'Points',
  '22': 'Ineligible Product',
  '23': 'Eligible Product',
  '24': 'Rx/IRIX Condition',
  '25': 'Decision Grade',
  '26': 'Unreconciled Conditions',
  '27': 'Ineligible Riders',
  '28': 'State',
  '29': 'Status',
  '30': 'Disclosed',
  '31': 'Value',
  // 32 is skipped (need_verify, unused in Janus)
  '33': 'Need Verification',
  '34': 'Src',
  '35': 'Src Code',
  '36': 'MIB Modifier',
  '37': 'MIB Timeletter',
  '38': 'MIB Code',
  '39': 'Value Min',
  '40': 'Value Max',
  '41': 'Percentage Addition',
  '42': 'Local Debits',
};

// --- Detection ---

export function isUwiqTracemap(data: any): boolean {
  const snapshots = data?.snapshot ?? [];
  return snapshots.some(
    (snap: any) => snap.decisionInformation?.mappedOutput != null,
  );
}

// --- Outcome helpers ---

function outcomeDecision(outcome: Record<string, string | string[]>): string {
  const d = outcome['0'];
  if (typeof d === 'string') return d;
  return 'unknown';
}

function outcomeDebit(
  outcome: Record<string, string | string[]>,
): number | null {
  const d = outcome['4'];
  if (typeof d === 'string') {
    const n = parseInt(d, 10);
    return isNaN(n) ? null : n;
  }
  return null;
}

function formatOutcomeValue(value: string | string[] | number): string {
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export function formatOutcomeSummary(
  outcomes: Record<string, string | string[] | number>,
): string[] {
  return Object.entries(outcomes)
    .filter(([key]) => key !== '0') // skip decision itself, it's shown via variant
    .map(([key, val]) => {
      const label = OUTCOME_KEYS[key] ?? `#${key}`;
      return `${label}: ${formatOutcomeValue(val)}`;
    })
    .filter(Boolean);
}

// --- Name formatting ---

export function formatStageName(name: string): string {
  // Keep (Superset) / (Workflow) indicator but title-case it
  return name
    .replace(/[_]/g, ' ')
    .replace(/\(superset\)/gi, '(Superset)')
    .replace(/\(workflow\)/gi, '(Workflow)')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(w =>
      w.startsWith('(') ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(' ');
}

function normalizeRulesetName(name: string): string {
  return name.toLowerCase();
}

// --- Extract hits ---

export function extractHits(snapshot: any): Map<string, HitInfo> {
  const hits = new Map<string, HitInfo>();
  const di = snapshot.decisionInformation;
  if (!di) return hits;

  const stages: Record<string, StageNode>[] = di.traceMap.stage_info;
  for (let si = 0; si < stages.length; si++) {
    const stage = stages[si];
    for (const [rawName, node] of Object.entries(stage)) {
      const normalized = normalizeRulesetName(rawName);
      const decision = outcomeDecision(node.outcome);
      const debit = outcomeDebit(node.outcome);
      hits.set(normalized, {
        decision,
        debit,
        outcomes: node.outcome as Record<string, string | string[] | number>,
        ruleIds: node.rules,
        stageIndex: si,
        rawName,
      });
    }
  }

  return hits;
}

// --- Build stage tree ---

export function buildStageTree(snapshot: any): StageTreeNode | null {
  const di = snapshot.decisionInformation;
  if (!di) return null;

  const stages: Record<string, StageNode>[] = di.traceMap.stage_info;
  const traceEntries: TraceEntry[] = di.traceMap.trace_map;

  // 1. Build a map of normalized stage name -> StageTreeNode
  const allNodes = new Map<string, StageTreeNode>();
  for (let si = 0; si < stages.length; si++) {
    const stage = stages[si];
    for (const [rawName, node] of Object.entries(stage)) {
      const name = rawName.toLowerCase();
      const isSuperset = name.includes('(superset)');
      allNodes.set(name, {
        name,
        displayName: formatStageName(rawName),
        decision: outcomeDecision(node.outcome),
        debit: outcomeDebit(node.outcome),
        ruleIds: node.rules,
        stageIndex: si,
        type: isSuperset ? 'superset' : 'workflow',
        children: [],
      });
    }
  }

  if (allNodes.size === 0) return null;

  // 2. Build rule_id -> stage name index
  const ruleToStageName = new Map<number, string>();
  for (const [, node] of allNodes) {
    for (const rid of node.ruleIds) {
      ruleToStageName.set(rid, node.name);
    }
  }

  // 3. Derive parent-child from trace_map src fields
  const childToParents = new Map<string, Set<string>>();

  for (const entry of traceEntries) {
    const parentName = ruleToStageName.get(entry.rule_id);
    if (!parentName) continue;

    for (const cond of entry.conditions) {
      if (cond.type === 'O' && cond.src && cond.src !== 'ANY') {
        const childName = cond.src.toLowerCase();
        if (allNodes.has(childName) && childName !== parentName) {
          if (!childToParents.has(childName)) {
            childToParents.set(childName, new Set());
          }
          childToParents.get(childName)!.add(parentName);
        }
      }
    }
  }

  // 4. Assign children
  const claimed = new Set<string>();
  for (const [childName, parentNames] of childToParents) {
    const firstParent = parentNames.values().next().value;
    if (!firstParent) continue;
    const parentNode = allNodes.get(firstParent);
    const childNode = allNodes.get(childName);
    if (parentNode && childNode) {
      if (!parentNode.children.some(c => c.name === childName)) {
        parentNode.children.push(childNode);
      }
      claimed.add(childName);
    }
  }

  // 5. Find root nodes (not claimed as child by anyone)
  const roots: StageTreeNode[] = [];
  for (const [name, node] of allNodes) {
    if (!claimed.has(name)) {
      roots.push(node);
    }
  }

  if (roots.length === 0) return null;
  if (roots.length === 1) return roots[0];

  // Wrap multiple roots
  const decisionName =
    snapshot.decision?.displayName ??
    snapshot.decision?.name ??
    'Decision';
  return {
    name: '__decision_root__',
    displayName: decisionName,
    decision: outcomeDecision({
      '0': snapshot.decision?.name?.toLowerCase() ?? 'unknown',
    }),
    debit: snapshot.debits ?? null,
    ruleIds: [],
    stageIndex: -1,
    type: 'superset',
    children: roots,
  };
}

// --- Convert StageTreeNode -> existing Node type for react-d3-tree ---

function decisionToVariant(
  decision: string,
): 'default' | 'success' | 'error' | 'warning' {
  const d = decision.toLowerCase();
  if (d === 'approve') return 'success';
  if (d === 'decline') return 'error';
  if (d === 'ruw') return 'warning';
  return 'default';
}

export function stageTreeToNode(
  stageNode: StageTreeNode,
  hits: Map<string, HitInfo>,
  traceEntries: TraceEntry[],
): Node {
  const hit = hits.get(stageNode.name);
  const decision = hit?.decision ?? stageNode.decision;
  const variant = decisionToVariant(decision);

  // Build subtitle from outcomes (excluding decision itself)
  const subtitle = hit
    ? formatOutcomeSummary(hit.outcomes)
    : [stageNode.type];

  // For workflow leaf nodes: attach triggered rules as children
  let children: Node[];
  if (stageNode.type === 'workflow' && stageNode.children.length === 0) {
    const ruleNodes: Node[] = stageNode.ruleIds
      .map(ruleId => traceEntries.find(e => e.rule_id === ruleId))
      .filter((entry): entry is TraceEntry => !!entry)
      .map(entry => ({
        id: uuidv4(),
        title: `Rule ${entry.rule_id}`,
        variant,
        subtitle: entry.conditions
          .map(c => {
            const matched = Array.isArray(c.matched_value)
              ? c.matched_value.join(', ')
              : c.matched_value;
            return `${c.fact_name}.${c.attribute_name} ${c.op} ${c.value} (matched: ${matched})`;
          })
          .join('; '),
      }));
    children = ruleNodes;
  } else {
    children = stageNode.children.map(c =>
      stageTreeToNode(c, hits, traceEntries),
    );
  }

  return {
    id: uuidv4(),
    title: stageNode.displayName,
    variant,
    subtitle,
    children,
  };
}

// --- Context label formatting ---

export function formatContextLabel(contextKey: string): string {
  return contextKey
    .replace(/^[^_]+_[^_]+_(?:mdm_)?/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// --- Main entry point for UWIQ mode ---

export function getUwiqNode(
  data: any,
  selectedSnapshotIdx: number,
): {
  node: Node | null;
  error: string | null;
  snapshots: SnapshotInfo[];
} {
  const errorMsg = 'Rule Tracemap Graph for this policy is not supported';

  try {
    const allSnapshots: any[] = data?.snapshot ?? [];

    // Filter to snapshots that have traceMap data
    const validSnapshots = allSnapshots.filter(
      (s: any) => s.decisionInformation?.traceMap,
    );

    if (validSnapshots.length === 0) {
      return { node: null, error: errorMsg, snapshots: [] };
    }

    // Build SnapshotInfo list for context selector
    const snapshots: SnapshotInfo[] = validSnapshots.map(
      (s: any, idx: number) => ({
        index: idx,
        contextKey: s.decisionInformation?.contextKey ?? `snapshot-${idx}`,
        contextDisplayName:
          s.decisionInformation?.contextDisplayName ??
          formatContextLabel(
            s.decisionInformation?.contextKey ?? `snapshot-${idx}`,
          ),
        decision:
          s.decision?.displayName ?? s.decision?.name ?? 'unknown',
        debits: s.debits ?? 0,
      }),
    );

    // Clamp selected index
    const clampedIdx = Math.min(selectedSnapshotIdx, validSnapshots.length - 1);
    const selectedSnapshot = validSnapshots[clampedIdx];

    // Build the stage tree
    const stageTree = buildStageTree(selectedSnapshot);
    if (!stageTree) {
      return { node: null, error: errorMsg, snapshots };
    }

    // Extract hits and trace entries
    const hits = extractHits(selectedSnapshot);
    const traceEntries: TraceEntry[] =
      selectedSnapshot.decisionInformation?.traceMap?.trace_map ?? [];

    // Convert to Node type for react-d3-tree
    const policyId =
      selectedSnapshot.policyId ?? data.snapshot?.[0]?.policyId ?? 'unknown';
    const rootNode: Node = {
      id: uuidv4(),
      title: `Policy ID ${policyId}`,
      variant: 'default',
      children: [stageTreeToNode(stageTree, hits, traceEntries)],
    };

    return { node: rootNode, error: null, snapshots };
  } catch (error) {
    return { node: null, error: errorMsg, snapshots: [] };
  }
}
